/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Grid from "@mui/material/Grid";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { format, isValid } from "date-fns";
import get from "lodash/get";

import { getROSignatories, getSignatories } from "../../../../../store/selectors/appConfig";
import {
  AutoSaveField as Field,
  SelectAutocomplete,
  DatePicker,
  Input
} from "../../../../../components/Form";
import { DATE_FORMAT, GCC_OR_UAE_NATIONALITY_LIST } from "../../../../../constants";

import { updateProspect } from "../../../../../store/actions/appConfig";
import { SubmitButton } from "../../../components/SubmitButton/SubmitButton";
import { Divider } from "../../../../../components/Divider";
import { RELATIONSHIP_EMPLOYER, RELATIONSHIP_SPOUSE, YES_NO_NA_OPTIONS } from "./constants";

import { NAME_REGEX, checkIsTrimmed } from "../../../../../utils/validation";
import { getInvalidMessage, getNotTrimmedMessage } from "../../../../../utils/getValidationMessage";
import { documentUnique } from "../../../../../constants/documents";
import { RoDocument } from "../../../../UploadDocuments/components/RoDocument/RoDocument";
import { getMinExpiryDate } from "../../../../../utils/helper";
import { ScrollToError } from "components/ScrollToError";

const nationalitySchema = Yup.object().shape({
  visaSponsorDifferentToCompany: Yup.string().nullable(),
  relationship: Yup.string(),
  isNocProvided: Yup.string(),
  others: Yup.string(),
  spouseName: Yup.string()
    .nullable()
    .max(80, "Maximum 80 characters allowed")
    .matches(NAME_REGEX, getInvalidMessage("Last name"))
    .test("space validation", getNotTrimmedMessage("Last name"), checkIsTrimmed),
  additionalComments: Yup.string().nullable(),
  passportExpiryDate: Yup.date()
    .nullable()
    .min(new Date(), getInvalidMessage("Passport Expiry Date"))
    .typeError(getInvalidMessage("Passport Expiry Date"))
});

const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

export const NationalityStep = ({
  index,
  handleContinue,
  createFormChangeHandler,
  sisterCompanyTL,
  signatoryDocs,
  nationality,
  visaSponserDiffFromProposedCom,
  stakeholder: { signatoryId }
}) => {
  const [initialValues, setInitialValues] = useState({
    visaSponsorDifferentToCompany: "",
    relationship: "",
    isNocProvided: "",
    others: "",
    spouseName: "",
    additionalComments: "",
    passportExpiryDate: ""
  });
  useEffect(() => {
    const isUAEOrGCC = nationality
      .flatMap(item => (item.nationality ? [item.nationality] : []))
      .some(ai => GCC_OR_UAE_NATIONALITY_LIST.includes(ai));
    if (isUAEOrGCC && visaSponserDiffFromProposedCom === "") {
      setInitialValues({ ...initialValues, visaSponsorDifferentToCompany: "NA" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const signatoryPath = `roProspect.signatoryInfo[${index}]`;

  return (
    <Formik
      onSubmit={handleContinue}
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={nationalitySchema}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values, errors, setFieldValue }) => (
        <Form>
          <ScrollToError />
          <Grid container spacing={3}>
            <Grid item sm={12} xs={12}>
              <h5> Visa </h5>
            </Grid>
            <Grid item sm={6} xs={12}>
              <p> Is Visa sponsor different from Proposed company? </p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="visaSponsorDifferentToCompany"
                path={`${signatoryPath}.visaSponserDiffFromProposedCom`}
                component={SelectAutocomplete}
                options={YES_NO_NA_OPTIONS}
                label="Please select"
                InputProps={{
                  inputProps: { tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <p> If yes, what is the relationship? </p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="relationship"
                path={`${signatoryPath}.visaSponsorRelationship.relationship`}
                label="Please select"
                component={SelectAutocomplete}
                datalistId="natRelationshipSA"
                tabIndex="0"
                isSearchable
              />
            </Grid>
          </Grid>
          <Divider.Dark />

          <Grid className="mt-25" container spacing={3}>
            {values.relationship === RELATIONSHIP_SPOUSE ? (
              <>
                <Grid item sm={12} xs={12}>
                  <RoDocument
                    uniqueId={documentUnique.SponsorPassportVisa}
                    stakeholderIndex={`${index}_${signatoryId}`}
                  />
                </Grid>
                <Grid item sm={12} xs={12} mt={2}>
                  <Field
                    name="passportExpiryDate"
                    placeholder="Passport Expiry Date"
                    label="Passport Expiry Date"
                    path={`${signatoryPath}.passportExpiryDate`}
                    minDate={getMinExpiryDate()}
                    component={DatePicker}
                    changeProspect={changeDateProspectHandler}
                    InputProps={{
                      inputProps: { tabIndex: 0 }
                    }}
                    disableFuture={false}
                  />
                </Grid>
              </>
            ) : values.relationship === RELATIONSHIP_EMPLOYER ? (
              <>
                <Grid item sm={12} xs={12}>
                  <h5> No Objection Certificate (NOC) </h5>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <p> Is the NOC provided? </p>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Field
                    name="isNocProvided"
                    path={`${signatoryPath}.nocProvided`}
                    component={SelectAutocomplete}
                    options={YES_NO_NA_OPTIONS}
                    label="Please select"
                    InputProps={{
                      inputProps: { tabIndex: 0 }
                    }}
                  />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <RoDocument
                    uniqueId={documentUnique.SponsorNOC}
                    stakeholderIndex={`${index}_${signatoryId}`}
                  />
                </Grid>
              </>
            ) : (
              <Grid item sm={12} xs={12}>
                <Field
                  name="others"
                  path={`${signatoryPath}.visaSponsorRelationship.others`}
                  label="Please Specify"
                  placeholder="Please Specify"
                  component={Input}
                  InputProps={{
                    inputProps: { maxLength: 50, tabIndex: 0 }
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Grid item sm={12} xs={12}>
            <h5> Home Country Details</h5>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Field
              name="spouseName"
              label="Spouse Name (if available)"
              path={`${signatoryPath}.spouseName`}
              placeholder="Spouse Name (if available)"
              component={Input}
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
          <Grid item sm={12} xs={12}>
            <h5> Friend/ Relative in your Home Country</h5>
          </Grid>
          <Grid item sm={12} xs={12}>
            <Field
              name="additionalComments"
              label="ANY ADDITIONAL COMMENTS AND OBSERVATIONS"
              path={`${signatoryPath}.additionalComments`}
              placeholder="ANY ADDITIONAL COMMENTS AND OBSERVATIONS"
              multiline
              rows="4"
              component={Input}
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>

          <SubmitButton />
        </Form>
      ))}
    </Formik>
  );
};

const mapStateToProps = (state, { index }) => ({
  signatoryDocs: get(getROSignatories(state)[index], "stakeholdersDocuments.documents", []),
  sisterCompanyTL: get(getROSignatories(state)[index], "sisterCompanyTL", ""),
  visaSponserDiffFromProposedCom: get(
    getROSignatories(state)[index],
    "visaSponserDiffFromProposedCom",
    ""
  ),
  nationality: get(getSignatories(state)[index], "passportDetails", [])
});

const mapDispatchToProps = {
  updateProspect
};

export const Nationality = connect(
  mapStateToProps,
  mapDispatchToProps
)(NationalityStep);
